import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  PropertyApartmentSubType,
  PropertyCommercialSubType,
  PropertyDefinitionDto,
  PropertyHouseSubType,
  PropertyLandSubType,
  PropertyParkingSubType,
  PropertyProfessionalSubType,
  PropertySubtype,
  PropertyType,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AuraNumberInputComponent } from '@workspace/ng-aura';
import { EtxeEnumsNiceLabelUtils } from '../../../../utils/etxe-enums-nice-label.utils';
import { EtxeEnumDisplayPipe } from '../../../pipes/etxe-enum-display.pipe';

export interface PropertyAccessibilityApartmentModel {
  floor: number | null;
  numberOfFloors: number | null;
}

export interface PropertyAccessibilityModel {
  apartment: PropertyAccessibilityApartmentModel;
  buildingNumberOfFloors: number | null;
  buildingHasElevator: boolean;
  pmrCompliance: boolean;
}

export interface PropertySubtypesModel {
  subtype: PropertySubtype | null;
  accessibility: PropertyAccessibilityModel;
}

export interface PropertySubtypesDto {
  propertyType?: PropertyType;
  subtype?: PropertySubtype;
  apartmentFloor?: number;
  apartmentNumberOfFloors?: number;
  buildingNumberOfFloors?: number;
  buildingHasElevator?: boolean;
  pmrCompliance?: boolean;
  accessibility?: {
    apartment?: {
      floor?: number;
      numberOfFloors?: number;
    };
    buildingNumberOfFloors?: number;
    buildingHasElevator?: boolean;
    pmrCompliance?: boolean;
  };
}

@Component({
  selector: 'etxe-property-subtypes',
  standalone: true,
  imports: [
    CommonModule,
    FormField,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonToggleModule,
    EtxeEnumDisplayPipe,
    AuraNumberInputComponent,
  ],
  templateUrl: './subtypes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertySubtypesComponent extends AbstractSignalFormComponent<
  PropertySubtypesDto,
  PropertySubtypesModel
> {
  PropertyType = PropertyType;
  propertyDefinition = input.required<PropertyDefinitionDto>();

  houseSubTypes = Object.values(PropertyHouseSubType);
  apartmentSubTypes = Object.values(PropertyApartmentSubType);
  commercialSubTypes = Object.values(PropertyCommercialSubType);
  landSubTypes = Object.values(PropertyLandSubType);
  parkingTypes = Object.values(PropertyParkingSubType);
  professionalSubTypes = Object.values(PropertyProfessionalSubType);

  getApartmentDescription(type: PropertyApartmentSubType): string {
    return EtxeEnumsNiceLabelUtils.apartmentDescription(type);
  }

  override model: WritableSignal<PropertySubtypesModel> = signal<PropertySubtypesModel>({
    subtype: null,
    accessibility: {
      apartment: {
        floor: null,
        numberOfFloors: null,
      },
      buildingNumberOfFloors: null,
      buildingHasElevator: false,
      pmrCompliance: false,
    },
  });

  override formTree = form(this.model, (b) => {
    required(b.subtype);
  });

  isFloorTypeLockedValue = computed(() => {
    const subType = this.model().subtype;
    return [
      PropertyApartmentSubType.SIMPLEX,
      PropertyApartmentSubType.DUPLEX,
      PropertyApartmentSubType.TRIPLEX,
      PropertyApartmentSubType.SOUFLEX,
    ].includes(subType as PropertyApartmentSubType);
  });

  isNumberOfFloorsLockedValue = computed(() => {
    const subType = this.model().subtype;
    return [
      PropertyApartmentSubType.SIMPLEX,
      PropertyApartmentSubType.DUPLEX,
      PropertyApartmentSubType.TRIPLEX,
    ].includes(subType as PropertyApartmentSubType);
  });

  minNumberOfFloorsValue = computed(() => {
    const subType = this.model().subtype;
    return subType === PropertyApartmentSubType.SOUFLEX ? 3 : 1;
  });

  onSubtypeChange(value: PropertySubtype | null) {
    this.model.update((m) => ({ ...m, subtype: value }));
    if (!value) return;
    if (this.propertyDefinition().propertyType === PropertyType.APARTMENT) {
      this.handleApartmentSubTypeChange(value as PropertyApartmentSubType);
    }
  }

  handleApartmentSubTypeChange(value: PropertyApartmentSubType) {
    switch (value) {
      case PropertyApartmentSubType.SIMPLEX:
        this.setFloor(0);
        break;
      case PropertyApartmentSubType.DUPLEX:
        this.setFloor(1);
        this.model.update((m) => ({
          ...m,
          accessibility: {
            ...m.accessibility,
            apartment: { ...m.accessibility.apartment, numberOfFloors: 1 },
          },
        }));
        break;
      case PropertyApartmentSubType.TRIPLEX:
        this.setFloor(1);
        this.model.update((m) => ({
          ...m,
          accessibility: {
            ...m.accessibility,
            apartment: { ...m.accessibility.apartment, numberOfFloors: 2 },
          },
        }));
        break;
      case PropertyApartmentSubType.SOUFLEX:
        this.setFloor(1);
        this.model.update((m) => ({
          ...m,
          accessibility: {
            ...m.accessibility,
            apartment: { ...m.accessibility.apartment, numberOfFloors: 3 },
          },
        }));
        break;
    }
  }

  setFloor(val: number) {
    if (val === 0) {
      this.model.update((m) => ({
        ...m,
        accessibility: {
          ...m.accessibility,
          apartment: {
            ...m.accessibility.apartment,
            numberOfFloors: 0,
            floor:
              this.propertyDefinition().propertyType === PropertyType.HOUSE
                ? 0
                : m.accessibility.apartment.floor,
          },
        },
      }));
    } else {
      this.model.update((m) => ({
        ...m,
        accessibility: {
          ...m.accessibility,
          apartment: {
            ...m.accessibility.apartment,
            numberOfFloors: m.accessibility.apartment.numberOfFloors || 1,
          },
        },
      }));
    }
  }

  protected override buildModelFromDto(dto: PropertySubtypesDto): PropertySubtypesModel {
    const rawFloors = dto?.accessibility?.apartment?.numberOfFloors ?? dto?.apartmentNumberOfFloors ?? null;
    const isHouse = this.propertyDefinition()?.propertyType === PropertyType.HOUSE;
    const isHouseOrApt = isHouse || this.propertyDefinition()?.propertyType === PropertyType.APARTMENT;
    const buildingFloors = dto?.accessibility?.buildingNumberOfFloors ?? dto?.buildingNumberOfFloors ?? null;
    const houseFloors = isHouse && buildingFloors != null ? Math.max(0, buildingFloors - 1) : null;

    return {
      subtype: dto?.subtype ?? null,
      accessibility: {
        apartment: {
          floor: dto?.accessibility?.apartment?.floor ?? dto?.apartmentFloor ?? null,
          numberOfFloors: rawFloors ?? houseFloors ?? (isHouseOrApt ? 0 : null),
        },
        buildingNumberOfFloors: buildingFloors,
        buildingHasElevator: dto?.accessibility?.buildingHasElevator ?? dto?.buildingHasElevator ?? false,
        pmrCompliance: dto?.accessibility?.pmrCompliance ?? dto?.pmrCompliance ?? false,
      },
    };
  }

  protected override buildDtoFromModel(model: PropertySubtypesModel): PropertySubtypesDto {
    const isHouseOrApt =
      this.propertyDefinition()?.propertyType === PropertyType.HOUSE ||
      this.propertyDefinition()?.propertyType === PropertyType.APARTMENT;
    const numberOfFloors = model.accessibility.apartment.numberOfFloors ?? (isHouseOrApt ? 0 : undefined);

    return {
      propertyType: this.dto()?.propertyType,
      subtype: model.subtype ?? undefined,
      apartmentFloor: model.accessibility.apartment.floor ?? undefined,
      apartmentNumberOfFloors: numberOfFloors,
      buildingNumberOfFloors: model.accessibility.buildingNumberOfFloors ?? undefined,
      buildingHasElevator: model.accessibility.buildingHasElevator,
      pmrCompliance: model.accessibility.pmrCompliance,
      accessibility: {
        apartment: {
          floor: model.accessibility.apartment.floor ?? undefined,
          numberOfFloors: numberOfFloors,
        },
        buildingNumberOfFloors: model.accessibility.buildingNumberOfFloors ?? undefined,
        buildingHasElevator: model.accessibility.buildingHasElevator,
        pmrCompliance: model.accessibility.pmrCompliance,
      },
    };
  }
}
